import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation } from '@/api/query'
import { useOnboarding } from '@/onboarding/context'
import { TONE_LABELS } from '@/onboarding/steps'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/** The values the API's `Literal` admits. Their labels live in `onboarding/steps.ts`. */
const TONES = ['professional_warm', 'direct_technical', 'friendly_casual', 'formal'] as const

const VIEWPOINTS = [
  { value: 'we', label: 'We / our team' },
  { value: 'i', label: 'I / my studio' },
  { value: 'third_person', label: 'Third person' },
] as const

type Tone = (typeof TONES)[number]
type Viewpoint = (typeof VIEWPOINTS)[number]['value']
type Voice = Schema<'BrandVoiceOut'>

/**
 * What the sample pane is showing.
 *
 * `stored` separates the paragraph the tenant approved from one they are trying
 * on, because the caption has to say which — a preview of unsaved settings that
 * read as the saved voice would be the panel telling a small lie about what the
 * next article will sound like.
 */
interface Preview {
  text: string
  source: string
  avoidedWords: readonly string[]
  stored: boolean
}

function storedPreview(voice: Voice | null): Preview | null {
  if (!voice?.sample) return null
  return { text: voice.sample, source: 'saved', avoidedWords: [], stored: true }
}

/** `BrandVoiceRequest.avoid_words` and `always_mention` are `max_length=1000`. */
const WORD_LIST_LIMIT = 1000

/** `BrandVoiceRequest.writing_sample` is `max_length=5000`. */
const SAMPLE_LIMIT = 5000

interface Settings {
  tone: Tone
  viewpoint: Viewpoint
  avoid_words: string
  always_mention: string
  /** SHELL-48. The tenant's own prose, not ours — see the panel docstring. */
  writing_sample: string
}

const DEFAULTS: Settings = {
  tone: 'professional_warm',
  viewpoint: 'we',
  avoid_words: '',
  always_mention: '',
  writing_sample: '',
}

function formOf(voice: Voice | null | undefined): Settings {
  if (!voice) return DEFAULTS
  return {
    tone: (voice.tone as Tone | undefined) ?? DEFAULTS.tone,
    viewpoint: (voice.viewpoint as Viewpoint | undefined) ?? DEFAULTS.viewpoint,
    avoid_words: voice.avoid_words.join(', '),
    always_mention: voice.always_mention.join(', '),
    writing_sample: voice.writing_sample ?? '',
  }
}

/**
 * Settings → Brand voice — S-D3's fifth panel, built in S-G1.
 *
 * **This panel is the drift `panels.ts` was carrying.** The entry read
 * `{ label: 'Brand voice', arrivesIn: 'S-B25' }` — disabled, and naming a
 * session that closed on 2026-08-11. The wizard has written this row since
 * S-B9 and content generation has read it since S-B25; the only thing missing
 * was a way to change it once the wizard was behind you, which is exactly what
 * S-D2's own copy promises: *"You can change it any time and it applies to the
 * next draft."*
 *
 * **It is the wizard's own endpoints, not new ones.** `GET /api/v1/onboarding`
 * carries `brand_voice` and is readable by every role; `PUT
 * /api/v1/onboarding/brand-voice` and `POST /api/v1/onboarding/brand-voice/sample`
 * are `tenant:update`. The voice is one singleton row per tenant, so a second
 * pair of routes over the same row would be two ways to write one thing — and
 * the PUT settling wizard step 6 again is a no-op for a tenant whose steps are
 * all settled (`_settle` leaves the cursor where it is when nothing follows).
 *
 * **The read is `useOnboarding()`, not a query of its own.** Settings sits
 * inside `OnboardingBoundary`, which already holds that GET for the wizard and
 * the shell's setup banner — a second request here would be the same object
 * fetched twice, and `adopt()` after a save is what keeps the banner from
 * telling somebody step 6 is outstanding a second after they saved it.
 *
 * **The stored sample is shown on arrival; nothing is generated.** The wizard
 * regenerates when it opens, because there the tenant is choosing a voice by
 * reading output. Here they already chose, and `set_brand_voice`'s own
 * docstring says why the sample is stored rather than recomputed: *"re-rendering
 * a settings screen would spend tokens to redisplay text the tenant has already
 * approved."* Sonnet 5 writes these under S-B25. *Regenerate* is a press.
 *
 * **A member sees it read-only rather than not at all**, the treatment
 * `BudgetPanel` gives the cap: the voice governs work they are doing, and the
 * server refuses their write regardless.
 */
export function BrandVoicePanel(): JSX.Element {
  const { membership } = useActiveTenant()
  const toast = useToast()
  const editable = may(membership, 'tenant:update')

  const { status, failure, state, adopt, reload } = useOnboarding()

  const [settings, setSettings] = useState<Settings>(DEFAULTS)
  const [stored, setStored] = useState<Voice | null | undefined>(undefined)
  const [sample, setSample] = useState<Preview | null>(null)

  // Seeded **during render**, not in an effect, and that is the difference
  // between a correct form and one that is briefly wrong. An effect runs after
  // the paint, so the fields would show the defaults for one frame and only
  // then the tenant's actual voice — visible as a flicker, and racy for anything
  // that reads the form as soon as it appears. React supports this pattern
  // directly: a setState during render of the same component re-renders before
  // anything reaches the screen.
  //
  // `stored` doubles as the seed marker (`undefined` = never seeded) and as what
  // *Save* compares against, so the button stays disabled until the form really
  // differs from the row. Re-seeding on every identity change of `brand_voice`
  // is deliberate: that is a save in another tab, or the `adopt` below.
  const voice = state ? (state.brand_voice ?? null) : undefined
  if (voice !== undefined && stored !== voice) {
    setStored(voice)
    setSettings(formOf(voice))
    setSample(storedPreview(voice))
  }

  const preview = useMutation((body: Settings) =>
    call(() => api.POST('/api/v1/onboarding/brand-voice/sample', { body })),
  )
  const save = useMutation((body: Settings) =>
    call(() => api.PUT('/api/v1/onboarding/brand-voice', { body })),
  )

  // `status` is 'error' only when nothing was ever loaded — the provider keeps
  // the last good state through a failed reload, which is why the guard is on
  // `state` rather than on the status alone.
  if (status === 'error' && failure) return <ErrorState failure={failure} onRetry={reload} />
  if (!state) return <Loading label="Loading your brand voice" />

  const configured = stored != null
  const saved = formOf(stored ?? null)
  const tooLong =
    settings.avoid_words.length > WORD_LIST_LIMIT ||
    settings.always_mention.length > WORD_LIST_LIMIT ||
    settings.writing_sample.length > SAMPLE_LIMIT
  const dirty =
    settings.tone !== saved.tone ||
    settings.viewpoint !== saved.viewpoint ||
    settings.avoid_words !== saved.avoid_words ||
    settings.always_mention !== saved.always_mention ||
    settings.writing_sample !== saved.writing_sample

  function update(patch: Partial<Settings>): void {
    setSettings((current) => ({ ...current, ...patch }))
    save.reset()
  }

  async function regenerate(): Promise<void> {
    const result = await preview.run(settings)
    if (!result.ok) return
    setSample({
      text: result.data.text,
      source: result.data.source,
      avoidedWords: result.data.avoided_words_used ?? [],
      stored: false,
    })
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (tooLong) return
    // A tenant who skipped the step is told "Save to make them yours", so for
    // them the defaults are something to save; for everybody else they are not.
    if (!dirty && configured) {
      toast.show({ kind: 'info', title: 'Nothing to save' })
      return
    }
    const result = await save.run(settings)
    if (!result.ok) return
    // Adopted rather than refetched: every write route answers with the whole
    // state, so the provider takes it and the shell's setup banner agrees with
    // this panel in the same render. The effect above then re-seeds the form and
    // the sample from that answer — which matters because the server normalises
    // the word lists, and "a,b" would otherwise stay dirty against the "a, b"
    // that was actually stored.
    adopt(result.data)
    toast.show({
      kind: 'success',
      title: 'Brand voice saved',
      body: 'The next draft we write follows it.',
    })
  }

  return (
    <form
      className="ar-card"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__header">
        <h2>Brand voice</h2>
        {editable ? null : <span className="ar-badge ar-badge--neutral">Read only</span>}
        <span className="ar-card__spacer" />
        {/*
          SHELL-59. S-D3's header right slot. The sentence was inside the body
          paragraph below, where it read as a caveat about the paragraph rather
          than as a statement about the card — which is what putting it beside
          the heading makes it.
        */}
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          Applies to the next draft
        </span>
      </div>

      <div className="ar-card__body ar-stack">
        {/* No intro sentence (parity P-VOICE-01): the mockup's body opens on
            the Tone / Point-of-view row. The not-configured alert below is a
            state the mockup does not draw and stays. */}
        {configured ? null : (
          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ○
            </span>
            <div className="ar-alert__body">
              <strong>You have not set a voice yet</strong>
              This step was skipped during setup, so we write in the defaults below. Save to make
              them yours.
            </div>
          </div>
        )}

        <InlineError failure={save.state.failure ?? preview.state.failure} />

        {/*
          SHELL-61. S-D3's layout: a two-column row for the two selects, then the
          three free-text fields full width beneath it. The panel had all four
          fields stacked in the left column of a two-column grid with the sample
          in the right, which made every text input half a card wide — including
          the writing sample, which is a paragraph.

          The sample panel keeps its place, below rather than beside: it is an
          extra the mockup does not draw (recorded as such in the parity review),
          and the fields it comments on now span the card.
        */}
        <div className="ar-grid ar-grid--2">
          <Field label="Tone">
            {(aria) => (
              <select
                className="ar-select"
                {...aria}
                disabled={!editable}
                value={settings.tone}
                onChange={(event) => {
                  update({ tone: event.target.value as Tone })
                }}
              >
                {TONES.map((tone) => (
                  <option key={tone} value={tone}>
                    {TONE_LABELS[tone]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Point of view">
            {(aria) => (
              <select
                className="ar-select"
                {...aria}
                disabled={!editable}
                value={settings.viewpoint}
                onChange={(event) => {
                  update({ viewpoint: event.target.value as Viewpoint })
                }}
              >
                {VIEWPOINTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>

        {/* SHELL-60. S-D3's label. "Words to avoid" describes a preference; this
            is a rule, and `app/seo/voice.py` enforces it as one. */}
        <TextField
          label="Never use these words"
          placeholder="cheap, budget, world-class"
          disabled={!editable}
          value={settings.avoid_words}
          error={
            settings.avoid_words.length > WORD_LIST_LIMIT
              ? 'That is longer than we can store. Keep it under 1,000 characters.'
              : undefined
          }
          onChange={(event) => {
            update({ avoid_words: event.target.value })
          }}
        />

        <TextField
          label="Always mention"
          placeholder="10-year warranty, in-house carpentry"
          disabled={!editable}
          value={settings.always_mention}
          error={
            settings.always_mention.length > WORD_LIST_LIMIT
              ? 'That is longer than we can store. Keep it under 1,000 characters.'
              : undefined
          }
          onChange={(event) => {
            update({ always_mention: event.target.value })
          }}
        />

        {/*
          SHELL-48. The tenant's own writing, and the strongest instruction on
          this card: `BrandVoice.describe` quotes it last, where a model reads it
          most recently, and prefers it over the sample we generated. Optional,
          and it says so in the label rather than in help text nobody reads.
        */}
        <Field
          label="Paste writing you like (optional)"
          error={
            settings.writing_sample.length > SAMPLE_LIMIT
              ? 'That is longer than we can store. Keep it under 5,000 characters.'
              : undefined
          }
        >
          {(aria) => (
            <textarea
              className="ar-textarea"
              {...aria}
              rows={5}
              disabled={!editable}
              value={settings.writing_sample}
              placeholder="A paragraph from your site or a brochure. We match its rhythm."
              onChange={(event) => {
                update({ writing_sample: event.target.value })
              }}
            />
          )}
        </Field>

        <div className="ar-panel ar-stack ar-stack--tight">
          <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>Sample</strong>

          {sample ? (
            <>
              <p style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)', lineHeight: 1.7 }}>
                {sample.text}
              </p>
              {sample.avoidedWords.length > 0 ? (
                <div className="ar-alert ar-alert--warning" role="alert">
                  <span className="ar-alert__icon" aria-hidden="true">
                    !
                  </span>
                  <div className="ar-alert__body">
                    <strong>This sample used a word you asked us to avoid</strong>
                    {sample.avoidedWords.join(', ')} — preview again, or drop the rule.
                  </div>
                </div>
              ) : null}
              <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
                {sample.stored
                  ? 'Written with your saved settings'
                  : `Generated with the settings above, unsaved · ${sample.source}`}
              </span>
            </>
          ) : (
            // Not a skeleton: nothing is loading. A tenant who skipped step 6
            // has no stored sample, and the honest answer is to say so and
            // offer the press that makes one.
            <p className="ar-muted" style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
              No sample yet.
              {editable
                ? ' Press Preview a sample to see how these settings read.'
                : ' An owner or admin can generate one.'}
            </p>
          )}
        </div>
      </div>

      {/*
        SHELL-49. S-D3 puts *Preview a sample* in the footer beside Save, and it
        was a `quiet-sm` *Regenerate* inside the sample panel — an action of the
        card, filed as an action of one pane of it. The verb is the mockup's
        too: on a card whose sample may not exist yet, "Regenerate" names
        something that has not happened.
      */}
      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        {editable ? (
          <Button
            tone="ghost"
            busy={preview.pending}
            busyLabel="Writing a sample"
            onClick={() => {
              void regenerate()
            }}
          >
            Preview a sample
          </Button>
        ) : null}
        <Button
          type="submit"
          tone="primary"
          // Enabled as drawn (parity P-VOICE-05); a save with nothing changed
          // writes nothing and says so, an over-long list is refused on the click.
          disabled={!editable}
          writes
          busy={save.state.status === 'running'}
        >
          Save
        </Button>
      </div>
    </form>
  )
}
